import { BodyText, Button, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { formatDate } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { useAddPatientTab } from "@/network/useAddPatientTab";
import type { DuplicatePatient } from "@/network/useDuplicatePatients";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog/Dialog";

const MAX_DUPLICATES = 50;

const formatLastVisit = (lastVisit?: string | null) => {
  if (lastVisit === undefined) return null;
  if (!lastVisit) return "No Visits";
  return `Last Visit: ${formatDate(lastVisit)}`;
};

const formatDuplicateId = (patient: DuplicatePatient): string => {
  const id = patient.mrn ? `ID: ${patient.mrn}` : patient.patientId;
  const name = [patient.firstName, patient.lastName].filter(Boolean).join(" ");
  return name ? `${id}, ${name}` : id;
};

interface DuplicateRecordDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  duplicatePatients: DuplicatePatient[];
  onOpenDuplicateChart: (patientId: string) => void;
}

export const DuplicateRecordDialog = ({
  isOpen,
  onOpenChange,
  duplicatePatients,
  onOpenDuplicateChart,
}: DuplicateRecordDialogProps) => {
  const addPatientTab = useAddPatientTab();

  const handleOpenChart = async (patientId: string) => {
    trackEvent(TRACKERS.OPEN_DUPLICATE_PATIENT_CHART);
    try {
      await addPatientTab.mutateAsync({ patientId });
      onOpenDuplicateChart(patientId);
    } catch {
      toast.error("Unable to open chart", {
        id: "duplicate-record-open-chart-error",
      });
    }
  };

  const visiblePatients = duplicatePatients.slice(0, MAX_DUPLICATES);
  const remainingCount = duplicatePatients.length - MAX_DUPLICATES;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Duplicate Medical Record Detected</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          This patient has duplicate medical records. Please review the
          following record(s) to ensure continuity of care:
        </DialogDescription>
        <BodyText size="xs" textColor="tertiary">
          Click to open in a new tab without leaving this chart
        </BodyText>
        <ul className="list-disc pl-6 space-y-1 max-h-64 overflow-y-auto">
          {visiblePatients.map((patient) => (
            <DuplicatePatientRow
              key={patient.patientId}
              patient={patient}
              onOpenChart={handleOpenChart}
            />
          ))}
        </ul>
        {remainingCount > 0 && (
          <BodyText size="sm" textColor="tertiary" className="mt-1">
            and {remainingCount} more duplicate record
            {remainingCount > 1 ? "s" : ""}
          </BodyText>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DuplicatePatientRow = ({
  patient,
  onOpenChart,
}: {
  patient: DuplicatePatient;
  onOpenChart: (patientId: string) => void;
}) => {
  const lastVisitText = formatLastVisit(patient.lastVisit);
  return (
    <li className="list-item">
      <div className="inline-flex items-center gap-1">
        <Button
          variant="link"
          size="sm"
          onClick={() => onOpenChart(patient.patientId)}
        >
          {formatDuplicateId(patient)}
        </Button>
        {lastVisitText !== null && (
          <BodyText size="sm" textColor="tertiary">
            {lastVisitText}
          </BodyText>
        )}
      </div>
    </li>
  );
};
